import React, { useEffect, useMemo, useState } from "react";
import { addSavedItem } from "./storage";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  compareCreatedAtDesc,
  normalizeDirectoryItem,
  timestampToMillis,
} from "./dataAdapters";
import { normalizeExternalUrl } from "./urlUtils";
import DirectorySubmissionForm, { DIRECTORY_SERVICE_TYPES } from "./DirectorySubmissionForm";

const DIRECTORY_COLLECTIONS = [
  "directoryServices",
  "directorySubmissions",
  "directory",
];

const SERVICE_TYPES = [
  "All",
  ...DIRECTORY_SERVICE_TYPES,
];

export default function Directory() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("All");
  const [search, setSearch] = useState("");
  const [localGuideMode, setLocalGuideMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setLoadError("");

    const rowsByCollection = new Map();
    const settledCollections = new Set();
    const failedCollections = new Set();

    const publishRows = () => {
      const rows = DIRECTORY_COLLECTIONS.flatMap(
        (sourceCollection) => rowsByCollection.get(sourceCollection) || []
      ).sort(compareCreatedAtDesc);
      setItems(rows);
    };

    const settle = (sourceCollection, error = null) => {
      if (settledCollections.has(sourceCollection)) return;
      settledCollections.add(sourceCollection);
      if (error) failedCollections.add(sourceCollection);

      if (settledCollections.size === DIRECTORY_COLLECTIONS.length) {
        setLoading(false);
        if (failedCollections.size === DIRECTORY_COLLECTIONS.length) {
          setLoadError("The directory is temporarily unavailable. Please try again shortly.");
        } else if (failedCollections.size > 0) {
          setLoadError("Some older directory entries could not be loaded.");
        }
      }
    };

    const unsubs = DIRECTORY_COLLECTIONS.map((sourceCollection) =>
      onSnapshot(
        query(
          collection(db, sourceCollection),
          where("status", "==", "approved")
        ),
        (snap) => {
          rowsByCollection.set(
            sourceCollection,
            snap.docs.map((doc) =>
              normalizeDirectoryItem(sourceCollection, doc.id, doc.data())
            )
          );
          publishRows();
          settle(sourceCollection);
        },
        (error) => {
          console.error(`Unable to load ${sourceCollection}`, error);
          rowsByCollection.set(sourceCollection, []);
          publishRows();
          settle(sourceCollection, error);
        }
      )
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [loadAttempt]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = items;

    if (type && type !== "All") {
      list = list.filter((x) => x.serviceType === type);
    }

    if (s) {
      list = list.filter((x) => {
        const hay = [
          x.name,
          x.description,
          x.serviceType,
          x.address,
          x.postcode,
          x.website,
          x.email,
          x.phone,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(s);
      });
    }

    if (localGuideMode) {
      list = list.filter((x) => x.localGuide === true || x.highlighted === true);
    }

    return list;
  }, [items, type, search, localGuideMode]);

  const handleSave = (item) => {
    addSavedItem({
      type: "directory",
      id: item.id,
      title: item.name || "Directory item",
      meta: item.serviceType || "",
      createdAt: Date.now(),
    });
    alert("Saved to your Profile (only visible to you).");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold">Directory</h1>
          <p className="text-white/70 mt-1">
            Find local services and support, or submit a listing for review.
          </p>
          <a href="#add-directory-listing" className="mt-3 inline-flex rounded-xl bg-[#E63946] px-4 py-2 text-sm font-semibold hover:opacity-90">
            Add a listing
          </a>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services, groups, keywords…"
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm w-72 max-w-full"
          />

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          >
            {SERVICE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <label className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 px-3 py-2 rounded-xl">
            <input
              type="checkbox"
              checked={localGuideMode}
              onChange={(e) => setLocalGuideMode(e.target.checked)}
            />
            Local Guide Mode
          </label>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-amber-100">
          <p>{loadError}</p>
          <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)} className="mt-3 rounded-lg border border-amber-100/30 bg-black/20 px-3 py-2 text-sm font-semibold">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-white/70">Loading…</div>
      ) : filtered.length === 0 ? (
        loadError ? null : (
          <div className="text-white/70">
            No results yet. Try a different search or filter.
          </div>
        )
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="rounded-2xl bg-white/5 border border-white/10 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-lg">
                    {item.name || "Unnamed service"}
                  </h3>
                  <p className="text-sm text-white/70 mt-1">
                    {item.description || "No description provided."}
                  </p>
                </div>

                {item.highlighted && (
                  <span className="text-xs px-2 py-1 rounded-full bg-[#E63946]/20 border border-[#E63946]/30">
                    Promoted
                  </span>
                )}
              </div>

              <div className="mt-3 text-sm text-white/80 space-y-1">
                {item.serviceType && (
                  <div>
                    <span className="text-white/60">Type:</span>{" "}
                    {item.serviceType}
                  </div>
                )}
                {item.address && (
                  <div>
                    <span className="text-white/60">Address:</span>{" "}
                    {item.address}
                  </div>
                )}
                {item.website && (
                  <div>
                    <span className="text-white/60">Website:</span>{" "}
                    <a
                      className="underline hover:text-white"
                      href={normalizeExternalUrl(item.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.website}
                    </a>
                  </div>
                )}
                {item.email && (
                  <div>
                    <span className="text-white/60">Email:</span>{" "}
                    <a className="underline hover:text-white" href={`mailto:${item.email}`}>
                      {item.email}
                    </a>
                  </div>
                )}
                {item.phone && (
                  <div>
                    <span className="text-white/60">Phone:</span> {item.phone}
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleSave(item)}
                  className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm"
                >
                  Save
                </button>
              </div>

              <div className="mt-3 text-xs text-white/50">
                {timestampToMillis(item.createdAt)
                  ? new Date(timestampToMillis(item.createdAt)).toLocaleString()
                  : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      <DirectorySubmissionForm />
    </div>
  );
}
